import type { ReactElement } from 'react';
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { PrivateQueueEntrySummary, PrivateQueueOptions, ShowTypeInfo } from '@fetmode/types';
import { useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import {
  computePrivateQueueEstimate,
  formatCoins,
  formatPrivateDiscountLabel,
} from '@fetmode/utils';
import { Button } from '@fetmode/ui';

export interface PrivateShowRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  privateTypes: ShowTypeInfo[];
  queueOptions: PrivateQueueOptions | null;
  queueType: 'VIP' | 'EXCLUSIVE';
  onQueueTypeChange: (type: 'VIP' | 'EXCLUSIVE') => void;
  durationMinutes: number | null;
  onDurationMinutesChange: (value: number | null) => void;
  timingMode: 'asap' | 'schedule';
  onTimingModeChange: (mode: 'asap' | 'schedule') => void;
  scheduledAt: string;
  onScheduledAtChange: (value: string) => void;
  isJoining: boolean;
  onJoin: () => void;
  joinError?: string | null;
  optionsError?: string | null;
  queueEntry: PrivateQueueEntrySummary | null;
  queueEntries: PrivateQueueEntrySummary[];
  showQueueList: boolean;
  onLeaveQueue: () => void;
  isSignedIn: boolean;
  modelInPrivateLabel: string | null;
  canRequest: boolean;
}

function formatQueueEntryDetails(entry: PrivateQueueEntrySummary): string {
  const duration =
    entry.durationMinutes === null
      ? t('common.show.privateShowDurationUntimed')
      : t('common.show.privateShowDurationBlock').replace(
          '{{minutes}}',
          String(entry.durationMinutes),
        );

  const timing = entry.scheduledAt
    ? t('common.show.privateShowScheduledFor').replace(
        '{{datetime}}',
        new Date(entry.scheduledAt).toLocaleString(),
      )
    : t('common.show.privateShowTimingAsap');

  return `${duration} · ${timing}`;
}

function toDatetimeLocalValue(date: Date): string {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

export function PrivateShowRequestModal({
  isOpen,
  onClose,
  privateTypes,
  queueOptions,
  queueType,
  onQueueTypeChange,
  durationMinutes,
  onDurationMinutesChange,
  timingMode,
  onTimingModeChange,
  scheduledAt,
  onScheduledAtChange,
  isJoining,
  onJoin,
  joinError = null,
  optionsError = null,
  queueEntry,
  queueEntries,
  showQueueList,
  onLeaveQueue,
  isSignedIn,
  modelInPrivateLabel,
  canRequest,
}: PrivateShowRequestModalProps): ReactElement | null {
  const { openAuthModal } = useAuthModal();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  const minScheduleAt = useMemo(() => {
    const leadMinutes = queueOptions?.minScheduleLeadMinutes ?? 30;
    return toDatetimeLocalValue(new Date(Date.now() + leadMinutes * 60_000));
  }, [queueOptions?.minScheduleLeadMinutes]);

  const estimate = useMemo(() => {
    if (!queueOptions) {
      return null;
    }

    const rate =
      queueType === 'EXCLUSIVE'
        ? queueOptions.exclusiveCoinsPerMinute
        : queueOptions.vipCoinsPerMinute;
    const discountPercent =
      durationMinutes === null
        ? 0
        : (queueOptions.durationTiers.find((tier) => tier.minutes === durationMinutes)
            ?.discountPercent ?? 0);

    return {
      rate,
      ...computePrivateQueueEstimate({
        ratePerMinute: rate,
        durationMinutes,
        discountPercent,
        untimedDepositMinutes: queueOptions.untimedDepositMinutes,
        queueDepositPercent: queueOptions.queueDepositPercent,
        preTipCoins:
          queueType === 'EXCLUSIVE' ? queueOptions.exclusivePreTipCoins : queueOptions.vipPreTipCoins,
      }),
    };
  }, [durationMinutes, queueOptions, queueType]);

  if (!isOpen) {
    return null;
  }

  const queueListSection =
    showQueueList ? (
      <section className="show-room-modal__section">
        <h3 className="show-room-modal__section-title">{t('common.show.privateQueueTitle')}</h3>
        {queueEntries.length === 0 ? (
          <p className="show-room-modal__muted">{t('common.show.privateQueueEmpty')}</p>
        ) : (
          <ul className="show-room-modal__queue-list">
            {queueEntries.map((entry) => (
              <li key={entry.id}>
                {t('common.show.privateQueueEntry')
                  .replace('{{position}}', String(entry.position))
                  .replace('{{username}}', entry.username)
                  .replace('{{type}}', entry.type)
                  .replace('{{details}}', formatQueueEntryDetails(entry))}
              </li>
            ))}
          </ul>
        )}
      </section>
    ) : null;

  const canSubmitSchedule =
    timingMode === 'asap' || (timingMode === 'schedule' && scheduledAt.length > 0);

  return createPortal(
    <div className="show-room-modal" role="dialog" aria-modal="true" aria-labelledby="private-show-modal-title">
      <button type="button" className="show-room-modal__backdrop" aria-label={t('common.show.privateShowModalClose')} onClick={onClose} />
      <div className="show-room-modal__panel">
        <header className="show-room-modal__header">
          <h2 id="private-show-modal-title" className="show-room-modal__title">
            {t('common.show.privateShowModalTitle')}
          </h2>
          <button type="button" className="show-room-modal__close" onClick={onClose} aria-label={t('common.show.privateShowModalClose')}>
            ×
          </button>
        </header>

        <div className="show-room-modal__body">
          {!isSignedIn ? (
            <>
              {queueListSection}
              <p style={{ margin: queueListSection ? '1rem 0 0' : 0 }}>
                <button
                  type="button"
                  className="show-room-modal__text-action"
                  onClick={() => {
                    onClose();
                    openAuthModal({ mode: 'register', intent: 'private' });
                  }}
                >
                  {t('common.show.privateShowSignIn')}
                </button>
              </p>
            </>
          ) : queueEntry ? (
            <>
              {queueListSection}
              <p style={{ margin: queueListSection ? '1rem 0 0' : 0 }}>
                {queueEntries.length <= 1
                  ? t('common.show.queueRequesting')
                  : queueEntry.position > 1
                    ? t('common.show.queueJoinedAhead')
                        .replace('{{position}}', String(queueEntry.position))
                        .replace('{{ahead}}', String(queueEntry.position - 1))
                    : t('common.show.queueJoined').replace(
                        '{{position}}',
                        String(queueEntry.position),
                      )}
              </p>
              <p className="show-room-modal__muted" style={{ margin: '0.5rem 0 0' }}>
                {formatQueueEntryDetails(queueEntry)}
              </p>
              <div style={{ marginTop: '1rem' }}>
                <Button
                  label={t('common.show.leaveQueue')}
                  variant="secondary"
                  disabled={isJoining}
                  onClick={onLeaveQueue}
                />
              </div>
            </>
          ) : modelInPrivateLabel ? (
            <>
              {queueListSection}
              <p className="show-room-modal__muted" style={{ margin: queueListSection ? '1rem 0 0' : 0 }}>
                {t('common.show.modelInPrivate').replace('{{type}}', modelInPrivateLabel)}
              </p>
            </>
          ) : canRequest ? (
            <>
              {queueListSection}

              {showQueueList ? (
                <p className="show-room-modal__muted" style={{ margin: queueListSection ? '0 0 1rem' : '0 0 1rem' }}>
                  {t('common.show.privateShowQueueHint')}
                </p>
              ) : null}

              <section className="show-room-modal__section">
                <h3 className="show-room-modal__section-title">{t('common.show.queueType')}</h3>
                <div className="show-room-modal__types">
                  {privateTypes
                    .filter((type) =>
                      type.type === 'EXCLUSIVE'
                        ? queueOptions?.exclusiveEnabled !== false
                        : queueOptions?.vipEnabled !== false,
                    )
                    .map((type) => (
                    <label
                      key={type.type}
                      className={`show-room-modal__type${queueType === type.type ? ' show-room-modal__type--active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="privateQueueType"
                        checked={queueType === type.type}
                        onChange={() => {
                          onQueueTypeChange(type.type as 'VIP' | 'EXCLUSIVE');
                        }}
                      />
                      <strong>{type.label}</strong>
                      <span>{type.description}</span>
                      {queueOptions ? (
                        <span className="show-room-modal__rate">
                          {t('common.show.privateShowPerMinute').replace(
                            '{{amount}}',
                            formatCoins(
                              type.type === 'EXCLUSIVE'
                                ? queueOptions.exclusiveCoinsPerMinute
                                : queueOptions.vipCoinsPerMinute,
                            ),
                          )}
                        </span>
                      ) : null}
                    </label>
                  ))}
                </div>
              </section>

              <section className="show-room-modal__section">
                <h3 className="show-room-modal__section-title">{t('common.show.privateShowTimingTitle')}</h3>
                <div className="show-room-modal__timing">
                  <button
                    type="button"
                    className={`show-room-modal__timing-card${timingMode === 'asap' ? ' show-room-modal__timing-card--active' : ''}`}
                    onClick={() => {
                      onTimingModeChange('asap');
                    }}
                  >
                    <strong>{t('common.show.privateShowTimingAsap')}</strong>
                    <span>{t('common.show.privateShowTimingAsapHint')}</span>
                  </button>
                  <button
                    type="button"
                    className={`show-room-modal__timing-card${timingMode === 'schedule' ? ' show-room-modal__timing-card--active' : ''}`}
                    onClick={() => {
                      onTimingModeChange('schedule');
                      if (scheduledAt.length === 0) {
                        onScheduledAtChange(minScheduleAt);
                      }
                    }}
                  >
                    <strong>{t('common.show.privateShowTimingSchedule')}</strong>
                    <span>
                      {t('common.show.privateShowTimingScheduleHint').replace(
                        '{{minutes}}',
                        String(queueOptions?.minScheduleLeadMinutes ?? 30),
                      )}
                    </span>
                  </button>
                </div>
                {timingMode === 'schedule' ? (
                  <label className="show-room-modal__schedule-field">
                    {t('common.show.privateShowScheduleAt')}
                    <input
                      className="fm-input"
                      type="datetime-local"
                      min={minScheduleAt}
                      value={scheduledAt}
                      onChange={(event) => {
                        onScheduledAtChange(event.target.value);
                      }}
                    />
                  </label>
                ) : null}
              </section>

              <section className="show-room-modal__section">
                <h3 className="show-room-modal__section-title">{t('common.show.privateShowDurationTitle')}</h3>
                <div className="show-room-modal__duration-grid">
                  <button
                    type="button"
                    className={`show-room-modal__duration${durationMinutes === null ? ' show-room-modal__duration--active' : ''}`}
                    onClick={() => {
                      onDurationMinutesChange(null);
                    }}
                  >
                    <strong>{t('common.show.privateShowDurationUntimed')}</strong>
                    <span>{t('common.show.privateShowDurationUntimedHint')}</span>
                  </button>
                  {(queueOptions?.durationTiers ?? []).map((tier) => {
                    const discountLabel = formatPrivateDiscountLabel(tier.discountPercent);
                    return (
                      <button
                        key={tier.minutes}
                        type="button"
                        className={`show-room-modal__duration${durationMinutes === tier.minutes ? ' show-room-modal__duration--active' : ''}`}
                        onClick={() => {
                          onDurationMinutesChange(tier.minutes);
                        }}
                      >
                        <strong>
                          {t('common.show.privateShowDurationBlock').replace(
                            '{{minutes}}',
                            String(tier.minutes),
                          )}
                        </strong>
                        {discountLabel ? (
                          <span className="show-room-modal__discount">
                            {t('common.show.privateShowDiscountBadge').replace(
                              '{{percent}}',
                              String(tier.discountPercent),
                            )}
                          </span>
                        ) : (
                          <span className="show-room-modal__muted">Standard rate</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>

              {estimate ? (
                <div className="show-room-modal__estimate">
                  <p>
                    {t('common.show.privateShowPerMinute').replace(
                      '{{amount}}',
                      formatCoins(estimate.rate),
                    )}
                  </p>
                  {(queueType === 'EXCLUSIVE'
                    ? (queueOptions?.exclusivePreTipCoins ?? 0)
                    : (queueOptions?.vipPreTipCoins ?? 0)) > 0 ? (
                    <p>
                      {t('common.show.privateShowPretip').replace(
                        '{{amount}}',
                        formatCoins(
                          queueType === 'EXCLUSIVE'
                            ? (queueOptions?.exclusivePreTipCoins ?? 0)
                            : (queueOptions?.vipPreTipCoins ?? 0),
                        ),
                      )}
                    </p>
                  ) : null}
                  {durationMinutes === null ? (
                    <p className="show-room-modal__muted">
                      {t('common.show.privateShowEstimateDeposit').replace(
                        '{{amount}}',
                        formatCoins(estimate.depositCoins),
                      )}
                    </p>
                  ) : (
                    <>
                      <p>
                        {t('common.show.privateShowEstimateTotal').replace(
                          '{{amount}}',
                          formatCoins(estimate.estimatedCoins),
                        )}
                      </p>
                      <p className="show-room-modal__muted">
                        {t('common.show.privateShowEstimateDeposit').replace(
                          '{{amount}}',
                          formatCoins(estimate.depositCoins),
                        )}
                      </p>
                    </>
                  )}
                </div>
              ) : null}

              {optionsError ? (
                <p className="fm-error" style={{ margin: '0.75rem 0 0' }}>
                  {optionsError}
                </p>
              ) : null}
              {joinError ? (
                <p className="fm-error" style={{ margin: '0.75rem 0 0' }}>
                  {joinError}
                </p>
              ) : null}

              <div style={{ marginTop: '1rem' }}>
                <Button
                  label={
                    isJoining
                      ? t('common.show.joinPrivateQueueJoining')
                      : !queueOptions
                        ? t('common.show.joinPrivateQueueLoading')
                        : t('common.show.joinPrivateQueue')
                  }
                  disabled={isJoining || !canSubmitSchedule || !queueOptions}
                  onClick={onJoin}
                />
              </div>
            </>
          ) : (
            queueListSection
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
